import styled from "@emotion/styled";
import { useRecurly } from "@recurly/react-recurly";
import { Mutator } from "final-form";
import setFieldTouched from "final-form-set-field-touched";
import { forwardRef, useState } from "react";
import { Field, Form } from "react-final-form";

import {
  Button,
  DesignToken,
  FieldGroup,
  FormField,
  Input,
  SpacingToken,
} from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { useBreakpoints } from "hooks/use-breakpoints";
import { IBilling } from "store/entities/billing";
import { withVar } from "themes/helpers";
import { compose, maxLength, required } from "utils/validators";

import { CardInputField } from "./CardInputField";
import { CountrySelectField } from "./CountrySelectField";
import { PostalCodeInputField } from "./PostalCodeInputField";
import {
  CardFormSubmissionErrors,
  ICardFormValues,
  generateCardFormSubmissionErrors,
  generateCreditCardFormInitialValues,
  getCardFormFieldError,
  hasCardFormFieldError,
  recurlyApiErrorFormFieldNames,
  requiredCardFormFieldsErrors,
} from "./helpers";
import { TaxPreview } from "./tax-preview";

interface IProps {
  billing: IBilling | null;
  isSaving?: boolean;
  expandable?: boolean;
  showButtons?: boolean;
  buttonsDisabled?: boolean;
  onSubmitSuccess(tokenId: string): void;
  onCancel?(): void;
}
export const SubscriptionCardForm = forwardRef<HTMLButtonElement, IProps>(
  ({ billing, isSaving, expandable, showButtons, buttonsDisabled, onCancel, onSubmitSuccess }, ref) => {
    const { isMobile } = useBreakpoints();
    const recurly = useRecurly();
    const [expanded, setExpanded] = useState(false);
    const inputSize = isMobile ? "large" : "medium";

    const extraFieldsHidden = expandable && !expanded;

    const handleToggleExpandForm = (): void => {
      setExpanded(!expanded);
    };

    const handleFormSubmit = async (values: ICardFormValues): Promise<CardFormSubmissionErrors> => {
      const formValues = { ...values };
      delete formValues.creditCardError;

      const getToken = (): Promise<CardFormSubmissionErrors> =>
        new Promise(resolve => {
          recurly.token(formValues, (error, token) => {
            if (error) {
              resolve(generateCardFormSubmissionErrors(error));
              return;
            }

            onSubmitSuccess(token.id);
            resolve({} as CardFormSubmissionErrors);
          });
        });

      const errors = await getToken();

      return errors;
    };

    return (
      <Wrapper>
        <Form
          onSubmit={handleFormSubmit}
          mutators={
            { setFieldTouched } as {
              [key: string]: Mutator<ICardFormValues, object>;
            }
          }
          initialValues={generateCreditCardFormInitialValues(billing)}
          render={({ handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <StyledFieldGroup inline>
                <Field
                  name="first_name"
                  validate={compose(
                    required(requiredCardFormFieldsErrors.first_name),
                    maxLength(`${recurlyApiErrorFormFieldNames.first_name} is too long (50 characters)`, 50)
                  )}
                  render={({ input, meta }) => (
                    <HalfWidthFormField
                      labelText="First name"
                      labelFor={input.name}
                      error={getCardFormFieldError(meta)}
                    >
                      <Input
                        {...input}
                        value={input.value as string}
                        id={input.name}
                        data-1p-ignore
                        autoComplete="off"
                        inputSize={inputSize}
                        error={hasCardFormFieldError(meta)}
                      />
                    </HalfWidthFormField>
                  )}
                />
                <Field
                  name="last_name"
                  validate={compose(
                    required(requiredCardFormFieldsErrors.last_name),
                    maxLength(`${recurlyApiErrorFormFieldNames.last_name} is too long (50 characters)`, 50)
                  )}
                  render={({ input, meta }) => (
                    <HalfWidthFormField labelText="Last name" labelFor={input.name} error={getCardFormFieldError(meta)}>
                      <Input
                        {...input}
                        value={input.value as string}
                        id={input.name}
                        inputSize={inputSize}
                        data-1p-ignore
                        autoComplete="off"
                        error={hasCardFormFieldError(meta)}
                      />
                    </HalfWidthFormField>
                  )}
                />
              </StyledFieldGroup>
              <StyledFieldGroup inline>
                <CardInputField />
              </StyledFieldGroup>
              <StyledFieldGroup inline>
                <CountrySelectField />
                <PostalCodeInputField />
              </StyledFieldGroup>

              {showButtons && <TaxPreview />}

              {!extraFieldsHidden && (
                <>
                  <Divider />
                  <StyledFieldGroup inline>
                    <Field
                      name="company"
                      validate={compose(
                        maxLength(`${recurlyApiErrorFormFieldNames.company} is too long (100 characters)`, 100)
                      )}
                      render={({ input, meta }) => (
                        <HalfWidthFormField
                          labelText="Company name (optional)"
                          labelFor={input.name}
                          error={getCardFormFieldError(meta)}
                        >
                          <Input
                            {...input}
                            value={input.value as string}
                            inputSize={inputSize}
                            id={input.name}
                            data-1p-ignore
                            autoComplete="off"
                            error={hasCardFormFieldError(meta)}
                          />
                        </HalfWidthFormField>
                      )}
                    />
                    <Field
                      name="vat_number"
                      validate={compose(
                        maxLength(`${recurlyApiErrorFormFieldNames.vat_number} is too long (20 characters)`, 20)
                      )}
                      render={({ input, meta }) => (
                        <HalfWidthFormField
                          labelText="VAT ID (optional)"
                          labelFor={input.name}
                          error={getCardFormFieldError(meta)}
                        >
                          <Input
                            {...input}
                            value={input.value as string}
                            inputSize={inputSize}
                            id={input.name}
                            data-1p-ignore
                            autoComplete="off"
                            error={hasCardFormFieldError(meta)}
                          />
                        </HalfWidthFormField>
                      )}
                    />
                  </StyledFieldGroup>
                  <StyledFieldGroup inline>
                    <Field
                      name="address1"
                      validate={compose(
                        maxLength(`${recurlyApiErrorFormFieldNames.address1} is too long (50 characters)`, 50)
                      )}
                      render={({ input, meta }) => (
                        <FullWidthFormField
                          labelText="Address (optional)"
                          labelFor={input.name}
                          error={getCardFormFieldError(meta)}
                        >
                          <Input
                            {...input}
                            value={input.value as string}
                            inputSize={inputSize}
                            id={input.name}
                            data-1p-ignore
                            autoComplete="off"
                            error={hasCardFormFieldError(meta)}
                          />
                        </FullWidthFormField>
                      )}
                    />
                  </StyledFieldGroup>
                  <StyledFieldGroup inline>
                    <Field
                      name="city"
                      validate={compose(
                        maxLength(`${recurlyApiErrorFormFieldNames.city} is too long (50 characters)`, 50)
                      )}
                      render={({ input, meta }) => (
                        <HalfWidthFormField
                          labelText="City (optional)"
                          labelFor={input.name}
                          error={getCardFormFieldError(meta)}
                        >
                          <Input
                            {...input}
                            value={input.value as string}
                            inputSize={inputSize}
                            id={input.name}
                            data-1p-ignore
                            autoComplete="off"
                            error={hasCardFormFieldError(meta)}
                          />
                        </HalfWidthFormField>
                      )}
                    />
                    <Field
                      name="state"
                      validate={compose(
                        maxLength(`${recurlyApiErrorFormFieldNames.state} is too long (50 characters)`, 50)
                      )}
                      render={({ input, meta }) => (
                        <HalfWidthFormField
                          labelText="State (optional)"
                          labelFor={input.name}
                          error={getCardFormFieldError(meta)}
                        >
                          <Input
                            {...input}
                            value={input.value as string}
                            inputSize={inputSize}
                            id={input.name}
                            data-1p-ignore
                            autoComplete="off"
                            error={hasCardFormFieldError(meta)}
                          />
                        </HalfWidthFormField>
                      )}
                    />
                  </StyledFieldGroup>
                </>
              )}
              <Actions>
                {expandable && (
                  <Button kind="link" onClick={handleToggleExpandForm}>
                    {expanded ? "Hide more details" : "Add more details"}
                  </Button>
                )}
                {showButtons && (
                  <Flex gap={withVar(SpacingToken.Spacing2)}>
                    <Button
                      data-testid="save-account-details-form"
                      kind="primary"
                      type="submit"
                      disabled={buttonsDisabled}
                      loading={isSaving}
                    >
                      Save changes
                    </Button>
                    <Button onClick={onCancel} disabled={buttonsDisabled}>
                      Cancel
                    </Button>
                  </Flex>
                )}
              </Actions>
              {ref && <HiddenTriggerSubmitButton type="submit" ref={ref} />}
            </form>
          )}
        />
      </Wrapper>
    );
  }
);

SubscriptionCardForm.displayName = "SubscriptionCardForm";

const Wrapper = styled.div``;

const StyledFieldGroup = styled(FieldGroup)`
  margin: 0 -8px;
`;

const Divider = styled.div`
  height: 1px;
  background-color: ${withVar(DesignToken.BorderBasicSecondary)};
  margin: 8px 0 20px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  padding-inline: ${withVar(SpacingToken.Spacing1)};
`;

const FullWidthFormField = styled(FormField)`
  width: 100%;
`;

const HalfWidthFormField = styled(FormField)`
  width: 50%;
`;

const HiddenTriggerSubmitButton = styled.button`
  display: none;
`;
